import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { InputText } from 'primereact/inputtext'; 
import { Calendar } from 'primereact/calendar'; 
import { InputTextarea } from 'primereact/inputtextarea'; 
import { Dropdown } from 'primereact/dropdown'; // Import Dropdown component

import Layout from "../../component/layout";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import { useFacilityStore } from "../../../../app/z-store/store";

// Define the URLs needed for this component
const getRFQResponsesUrl = "/api/app/rfq-responses/all_responses"; 
const getRFQDetailsUrl = "/api/app/quotations/details"; 
const updateRFQResponseUrl = "/api/app/rfq-responses"; 
const awardRFQResponseUrl = "/api/app/rfq-responses/award"; 
const getPurchaseRequestsUrl = "/api/app/purchase-requests/all"; 


const RFQResponses = () => {
  const navigate = useNavigate();
  const { rfqId } = useParams(); // Get the rfqId from URL params
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  // State
  const [loading, setLoading] = useState(false);
  const [rfqDetails, setRfqDetails] = useState(null);
  const [responses, setResponses] = useState([]);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [awardModalVisible, setAwardModalVisible] = useState(false); // New state for award modal
  const [currentResponse, setCurrentResponse] = useState(null);
  const [rfqClosed, setRfqClosed] = useState(false);
  const [purchaseRequests, setPurchaseRequests] = useState([]);  // State for available PRs
  
  // New state for PO details
  const [poDetails, setPoDetails] = useState({
    prNumber: "",
    department: "",
    currency: "",
    date: new Date(),
    internalNotes: "",
    supplierNotes: "",
    budget: "",
    selectedPurchaseRequest: null // New field for storing selected PR object
  });

  // Fetch RFQ details
  const fetchRFQDetails = useCallback(async () => {
    if (!selectedFacilityId || !rfqId) return;

    try {
      setLoading(true);
      const response = await makeRequest2(
        `${getRFQDetailsUrl}/${selectedFacilityId}/${rfqId}`,
        "GET"
      );

      if (response.success && response.data) {
        setRfqDetails(response.data.data);
        
        // Initialize PO details with data from RFQ
        setPoDetails(prev => ({
          ...prev,
          department: response.data.data.department || "",
          currency: response.data.data.currency || "KES",
          internalNotes: response.data.data.notes || ""
        }));

        // Check if RFQ is closed (current date is after closing date)
        const closingDate = new Date(response.data.data.closingDate);
        const currentDate = new Date();
        setRfqClosed(currentDate > closingDate);
      } else {
        throw new Error("Failed to fetch RFQ details");
      }
    } catch (error) {
      toastify(error.message || "Error fetching RFQ details", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedFacilityId, rfqId]);

  // Fetch RFQ responses
  const fetchResponses = useCallback(async () => {
    if (!selectedFacilityId || !rfqId || !rfqClosed) return;

    try {
      setLoading(true);
      const response = await makeRequest2(
        `${getRFQResponsesUrl}/${selectedFacilityId}?rfqId=${rfqId}`,
        "GET"
      );

      // Check if response is successful and has data property which is an array
      if (response.success && response.data) {
        // Handle the case where data is an array with responses
        if (Array.isArray(response.data)) {
          setResponses(response.data);
        }
        // Handle case where data contains an array of responses
        else if (Array.isArray(response.data.data)) {
          setResponses(response.data.data);
        }
        // If data is an object with a single response
        else if (typeof response.data === 'object') {
          setResponses([response.data]);
        }
        else {
          setResponses([]);
          console.warn("Unexpected response format:", response);
        }
      } else {
        setResponses([]);
        throw new Error("Failed to fetch RFQ responses");
      }
    } catch (error) {
      toastify(error.message || "Error fetching RFQ responses", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedFacilityId, rfqId, rfqClosed]);

  // Fetch Purchase Requests
  const fetchPurchaseRequests = useCallback(async () => {
    if (!selectedFacilityId) return;
    
    try {
      setLoading(true);
      const response = await makeRequest2(
        `${getPurchaseRequestsUrl}/${selectedFacilityId}`,
        "GET"
      );
      
      if (response.success && response.data) {
        // Filter PRs that have been approved but don't have PO yet
        const approvedPRs = Array.isArray(response.data.data) 
          ? response.data.data.filter(pr => 
              ( pr.status === "approved") && 
              ( pr.poStatus === "pending"))
          : [];
        
        setPurchaseRequests(approvedPRs);
      } else {
        setPurchaseRequests([]);
      }
    } catch (error) {
      console.error("Error fetching purchase requests:", error);
      toastify("Error loading purchase requests", "error");
      setPurchaseRequests([]);
    } finally {
      setLoading(false);
    }
  }, [selectedFacilityId]);

  // Load data on component mount
  useEffect(() => {
    if (selectedFacilityId && rfqId) {
      fetchRFQDetails();
      fetchPurchaseRequests(); // Fetch purchase requests when component mounts
    }
  }, [selectedFacilityId, rfqId, fetchRFQDetails, fetchPurchaseRequests]);

  // Load responses once we know if RFQ is closed
  useEffect(() => {
    if (rfqClosed && selectedFacilityId && rfqId) {
      fetchResponses();
    }
  }, [rfqClosed, selectedFacilityId, rfqId, fetchResponses]);

  // Handle view response details
  const handleViewResponse = (response) => {
    setCurrentResponse(response);
    setViewModalVisible(true);
  };

  // Handle showing award modal
  const handleShowAwardModal = (response) => {
    setCurrentResponse(response);
    
    // Pre-fill the PO details with data from the response
    setPoDetails(prev => ({
      ...prev,
      supplierNotes: response.notes || "",
      selectedPurchaseRequest: null, // Reset selected PR
      // Keep other fields as they are or set defaults
    }));
    
    setAwardModalVisible(true);
  };

  // Handle PO details change
  const handlePoDetailsChange = (e) => {
    const { name, value } = e.target;
    setPoDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle PR selection
  const handlePRSelection = (e) => {
    const selectedPR = e.value;
    
    setPoDetails(prev => ({
      ...prev,
      selectedPurchaseRequest: selectedPR,
      prNumber: selectedPR?.irfNumber || "",
      department: selectedPR?.department || prev.department,
      budget: selectedPR?.budgetAvailable || prev.budget
    }));
  };

  // Handle award response and create PO
  const handleAwardResponseAndCreatePO = async () => {
    if (!currentResponse || !rfqDetails) return;

    try {
      setLoading(true);

      // Prepare data for award API - now including PR ID if selected
      const awardData = {
        rfqId,
        supplierId: currentResponse.supplierId,
        prId: poDetails.selectedPurchaseRequest?._id, // Include PR ID if a PR is selected
        ...poDetails
      };

      // Call the award API that creates a PO
      const apiResponse = await makeRequest2(
        `${awardRFQResponseUrl}/${selectedFacilityId}`,
        "POST",
        awardData
      );

      if (apiResponse.success) {
        toastify("RFQ awarded and Purchase Order created successfully", "success");
        setAwardModalVisible(false);
        fetchResponses(); // Refresh the responses list
        
        // Navigate to the PO view if available
        if (apiResponse.data && apiResponse.data.data && apiResponse.data.data.purchaseOrder) {
          // Optional: navigate to view the created PO
          // navigate(`/facility/procurement_management/purchase_orders/${apiResponse.data.data.purchaseOrder._id}`);
        }
      } else {
        toastify(apiResponse.error || "Failed to award RFQ and create Purchase Order", "error");
      }
    } catch (error) {
      toastify(error.message || "Error awarding RFQ", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle decline response
  const handleDeclineResponse = (response) => {
    confirmDialog({
      message: `Are you sure you want to decline this response from ${response.supplierDetails?.name || 'this supplier'}?`,
      header: 'Decline Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          setLoading(true);

          const updateData = {
            status: "declined" // Update status to declined
          };

          const apiResponse = await makeRequest2(
            `${updateRFQResponseUrl}/${selectedFacilityId}/${response._id}`,
            "PUT",
            updateData
          );

          if (apiResponse.success) {
            toastify("Response declined successfully", "success");
            fetchResponses(); // Refresh the responses list
          } else {
            toastify(apiResponse.error || "Failed to decline response", "error");
          }
        } catch (error) {
          toastify(error.message || "Error declining response", "error");
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Status template for the DataTable
  const statusTemplate = (rowData) => {
    const statusStyles = {
      pending: { color: '#ffc107', backgroundColor: '#fff8e1' },
      submitted: { color: '#28a745', backgroundColor: '#e9fbe9' },
      evaluated: { color: '#17a2b8', backgroundColor: '#e3f2fd' },
      awarded: { color: '#6f42c1', backgroundColor: '#f5e9ff' },
      declined: { color: '#dc3545', backgroundColor: '#ffebee' }
    };

    const status = rowData.status || "pending";
    const style = statusStyles[status] || {
      color: '#6c757d',
      backgroundColor: '#f1f1f1',
    };

    return (
      <span
        style={{
          ...style,
          padding: '0.5em 1em',
          borderRadius: '12px',
          fontWeight: 'bold',
          fontSize: '0.9rem',
          display: 'inline-block',
        }}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Date template for the DataTable
  const dateTemplate = (rowData) => {
    return new Date(rowData.createdAt).toLocaleDateString();
  };

  const evaluationScoreTemplate = (rowData) => {
    return rowData.evaluation && typeof rowData.evaluation.score === 'number'
      ? `${rowData.evaluation.score}%`
      : 'N/A';
  };

  // Price summary template for the DataTable
  const priceSummaryTemplate = (rowData) => {
    if (!rowData.items || !Array.isArray(rowData.items) || rowData.items.length === 0) {
      return 'N/A';
    }

    // Calculate total
    const totalAmount = rowData.items.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
    return `${rfqDetails?.currency || 'KES'} ${totalAmount.toLocaleString()}`;
  };

  // Items count template for the DataTable
  const itemsCountTemplate = (rowData) => {
    if (!rowData.items || !Array.isArray(rowData.items)) {
      return '0 items';
    }
    return `${rowData.items.length} item${rowData.items.length !== 1 ? 's' : ''}`;
  };

  // Actions template for the DataTable
  const actionBodyTemplate = (rowData) => {
    const isAwarded = rowData.status === 'awarded';
    const isDeclined = rowData.status === 'declined';

    return (
      <div className="d-flex gap-2">
        <Button
          icon="ti ti-eye"
          className="p-button-rounded p-button-info"
          onClick={() => handleViewResponse(rowData)}
          tooltip="View Details"
        />
        {!isAwarded && !isDeclined && (
          <>
            <Button
              icon="ti ti-award"
              className="p-button-rounded p-button-success"
              onClick={() => handleShowAwardModal(rowData)}
              tooltip="Award & Create PO"
            />
            <Button
              icon="ti ti-x"
              className="p-button-rounded p-button-danger"
              onClick={() => handleDeclineResponse(rowData)}
              tooltip="Decline"
            />
          </>
        )}
      </div>
    );
  };

  // View Modal Footer
  const viewModalFooter = (
    <Button
      label="Close"
      icon="pi pi-times"
      className="p-button-text"
      onClick={() => setViewModalVisible(false)}
    />
  );

  // Award Modal Footer
  const awardModalFooter = (
    <>
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => setAwardModalVisible(false)}
      />
      <Button
        label="Award & Create PO"
        icon="pi pi-check"
        className="p-button-success"
        onClick={handleAwardResponseAndCreatePO}
        loading={loading}
      />
    </>
  );

  // PR item template for dropdown
  const prItemTemplate = (option) => {
    if (!option) return "Select a Purchase Request (Optional)";
    
    return (
      <div className="d-flex flex-column">
        <span>{option.irfNumber} - {option.department}</span>
        <small className="text-muted">{new Date(option.date).toLocaleDateString()}</small>
      </div>
    );
  };

  // PR value template for dropdown
  const prValueTemplate = (option) => {
    if (!option) return "Select a Purchase Request (Optional)";
    
    return (
      <span>{option.irfNumber}</span>
    );
  };

  // Render message when RFQ is not closed yet
  const renderRFQNotClosedMessage = () => {
    if (!rfqDetails) return null;

    const closingDate = new Date(rfqDetails.closingDate);
    const formattedDate = closingDate.toLocaleDateString();
    const formattedTime = closingDate.toLocaleTimeString();

    return (
      <div className="alert alert-warning">
        <div className="d-flex align-items-center">
          <i className="ti ti-clock-hour-4 me-2 fs-4"></i>
          <div>
            <h5>RFQ Responses Not Available Yet</h5>
            <p className="mb-0">
              This RFQ is still open for submissions. Responses will be available for review after the closing date:
              <strong> {formattedDate} at {formattedTime}</strong>.
            </p>
          </div>
        </div>
      </div>
    );
  };

  if (!selectedFacilityId) {
    return (
      <Layout>
        <div className="card">
          <div className="card-body text-center">
            Please select a facility to view RFQ responses.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Page Header with Breadcrumbs */}
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/facility/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/facility/procurement_management/quotations_management">Requests For Quotation</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="">Responses</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main Card */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <Link to="/facility/procurement_management/quotations_management">
                <i className="ti ti-arrow-narrow-left"></i> Back
              </Link>
            </div>
          </div>
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5>
                RFQ Responses
                {rfqDetails && (
                  <span className="text-muted ms-2">
                    for {rfqDetails.name} ({rfqDetails.rfqNumber})
                  </span>
                )}
              </h5>
            </div>
            <div className="card-body">
              <ConfirmDialog />

              {/* RFQ Summary */}
              {rfqDetails && (
                <div className="alert alert-info mb-4">
                  <div className="row">
                    <div className="col-md-4">
                      <strong>RFQ Number:</strong> {rfqDetails.rfqNumber}
                    </div>
                    <div className="col-md-4">
                      <strong>Status:</strong> {rfqDetails.status}
                    </div>
                    <div className="col-md-4">
                      <strong>Category:</strong> {rfqDetails.rfqType || rfqDetails.category}
                    </div>
                  </div>
                  <div className="row mt-2">
                    <div className="col-md-4">
                      <strong>Start Date:</strong> {new Date(rfqDetails.startDate).toLocaleDateString()}
                    </div>
                    <div className="col-md-4">
                      <strong>Closing Date:</strong> {new Date(rfqDetails.closingDate).toLocaleDateString()}
                    </div>
                    <div className="col-md-4">
                      <strong>Total Responses:</strong> {rfqClosed ? responses.length : "Not available yet"}
                    </div>
                  </div>
                </div>
              )}

              {/* Display message if RFQ is not closed */}
              {!rfqClosed && renderRFQNotClosedMessage()}

              {/* DataTable - Only shown if RFQ is closed */}
              {rfqClosed && (
                <DataTable
                  value={responses}
                  paginator
                  rows={10}
                  loading={loading}
                  emptyMessage="No responses found for this RFQ"
                  sortMode="multiple"
                  removableSort
                  stripedRows
                  tableStyle={{ minWidth: '50rem' }}
                >
                  <Column
                    field="createdAt"
                    header="Submitted Date"
                    sortable
                    body={dateTemplate}
                  />
                  <Column
                    field="supplierDetails.name"
                    header="Supplier Name"
                    sortable
                    body={(rowData) => rowData.supplierDetails?.name || 'Unknown Supplier'}
                  />
                  <Column
                    header="Items"
                    body={itemsCountTemplate}
                  />
                  <Column
                    header="Total Quote"
                    body={priceSummaryTemplate}
                    sortable
                  />
                  <Column
                    field="status"
                    header="Status"
                    sortable
                    body={statusTemplate}
                  />
                  <Column field="evaluation.score" header="Evaluation Score" body={evaluationScoreTemplate} sortable />
                  <Column
                    header="Actions"
                    body={actionBodyTemplate}
                    style={{ width: '12rem' }}
                  />
                </DataTable>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* View Response Modal */}
      <Dialog
        header="Response Details"
        visible={viewModalVisible}
        style={{ width: '80vw' }}
        footer={viewModalFooter}
        onHide={() => setViewModalVisible(false)}
      >
        {currentResponse && (
          <div className="p-fluid">
            <div className="row mb-3">
              <div className="col-md-6">
                <h6>Supplier Information</h6>
                <div className="d-flex flex-column gap-2">
                  <div>
                    <strong>Name:</strong> {currentResponse.supplierDetails?.name || 'Unknown'}
                  </div>
                  <div>
                    <strong>Email:</strong> {currentResponse.supplierDetails?.email || 'N/A'}
                  </div>
                  <div>
                    <strong>Phone:</strong> {currentResponse.supplierDetails?.phone || 'N/A'}
                  </div>
                  <div>
                    <strong>Contact Person:</strong> {currentResponse.supplierDetails?.contactPerson || 'N/A'}
                  </div>
                  <div>
                    <strong>Department:</strong> {currentResponse.supplierDetails?.department || 'N/A'}
                  </div>
                  <div>
                    <strong>Submission Date:</strong> {new Date(currentResponse.createdAt).toLocaleString()}
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <h6>Response Status</h6>
                <div className="d-flex flex-column gap-2">
                  <div>
                    <strong>Status:</strong> {currentResponse.status}
                  </div>
                  <div>
                    <strong>Total Items:</strong> {currentResponse.items?.length || 0}
                  </div>
                  <div>
                    <strong>Currency:</strong> {rfqDetails?.currency || 'KES'}
                  </div>
                  {currentResponse.items && currentResponse.items.length > 0 && (
                    <div>
                      <strong>Total Quote Amount:</strong> {`${rfqDetails?.currency || 'KES'} ${currentResponse.items.reduce((sum, item) => sum + (item.totalPrice || 0), 0).toLocaleString()}`}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-12">
                <h6>Quoted Items</h6>
                {currentResponse.items && currentResponse.items.length > 0 ? (
                  <DataTable value={currentResponse.items} stripedRows>
                    <Column field="itemId" header="Item ID" />
                    <Column field="itemName" header="Item Name" body={rowData => rowData.itemName || 'N/A'} />
                    <Column field="quantity" header="Quantity" />
                    <Column field="unitPrice" header="Unit Price" body={rowData =>
                      `${rfqDetails?.currency || 'KES'} ${(rowData.unitPrice || 0).toLocaleString()}`
                    } />
                    <Column field="totalPrice" header="Total Price" body={rowData =>
                      `${rfqDetails?.currency || 'KES'} ${(rowData.totalPrice || 0).toLocaleString()}`
                    } />
                    <Column field="notes" header="Notes" body={rowData => rowData.notes || 'N/A'} />
                  </DataTable>
                ) : (
                  <p>No items in this response.</p>
                )}
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-12">
                <h6>Notes</h6>
                <p>{currentResponse.notes || "No notes provided."}</p>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-12">
                <h6>Attachments</h6>
                {currentResponse.attachments && currentResponse.attachments.length > 0 ? (
                  <DataTable value={currentResponse.attachments} stripedRows>
                    <Column field="fileName" header="File Name" />
                    <Column field="uploadedAt" header="Upload Date" body={rowData =>
                      new Date(rowData.uploadedAt || rowData.createdAt || Date.now()).toLocaleString()
                    } />
                    <Column header="Action" body={rowData => (
                      <Button
                        label="Download"
                        icon="pi pi-download"
                        className="p-button-sm"
                        onClick={() => window.open(rowData.filePath, '_blank')}
                      />
                    )} />
                  </DataTable>
                ) : (
                  <p>No attachments provided.</p>
                )}
              </div>
            </div>

            {!currentResponse.status.includes('awarded') && !currentResponse.status.includes('declined') && (
              <div className="row mb-3">
                <div className="col-md-12 d-flex justify-content-end gap-2">
                  <Button
                    label="Decline"
                    icon="pi pi-times"
                    className="p-button-danger"
                    onClick={() => {
                      setViewModalVisible(false);
                      handleDeclineResponse(currentResponse);
                    }}
                  />
                  <Button
                    label="Award & Create PO"
                    icon="pi pi-check"
                    className="p-button-success"
                    onClick={() => {
                      setViewModalVisible(false);
                      handleShowAwardModal(currentResponse);
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </Dialog>

      {/* Award and Create PO Modal */}
      <Dialog
        header="Award RFQ & Create Purchase Order"
        visible={awardModalVisible}
        style={{ width: '60vw' }}
        footer={awardModalFooter}
        onHide={() => setAwardModalVisible(false)}
      >
        {currentResponse && (
          <div className="p-fluid">
            <div className="alert alert-info mb-4">
              <div className="d-flex align-items-center mb-2">
                <i className="ti ti-info-circle me-2 fs-5"></i>
                <strong>You are about to award this RFQ to {currentResponse.supplierDetails?.name || 'this supplier'} and create a Purchase Order</strong>
              </div>
              <p className="mb-0">
                A Purchase Order will be automatically generated using details from the RFQ and supplier's response.
                Please fill in the additional information below.
              </p>
            </div>

            {/* Purchase Request Dropdown */}
            <div className="row mb-3">
              <div className="col-md-12">
                <label htmlFor="selectedPurchaseRequest" className="form-label">Link to Purchase Request</label>
                <Dropdown
                  id="selectedPurchaseRequest"
                  value={poDetails.selectedPurchaseRequest}
                  options={purchaseRequests}
                  onChange={handlePRSelection}
                  optionLabel="irfNumber"
                  filter
                  filterBy="irfNumber,department"
                  placeholder="Select a Purchase Request (Optional)"
                  itemTemplate={prItemTemplate}
                  valueTemplate={prValueTemplate}
                  className="w-100"
                  emptyMessage="No approved Purchase Requests available"
                />
                <small className="text-muted">
                  Linking a Purchase Request will automatically populate some fields and link the PO with the PR.
                </small>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="prNumber" className="form-label">PR Number</label>
                <InputText
                  id="prNumber"
                  name="prNumber"
                  value={poDetails.prNumber}
                  onChange={handlePoDetailsChange}
                  placeholder="Enter PR Number if available"
                  disabled={!!poDetails.selectedPurchaseRequest}
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="department" className="form-label">Department *</label>
                <InputText
                  id="department"
                  name="department"
                  value={poDetails.department}
                  onChange={handlePoDetailsChange}
                  required
                  placeholder="Enter department"
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="currency" className="form-label">Currency</label>
                <InputText
                  id="currency"
                  name="currency"
                  value={poDetails.currency}
                  onChange={handlePoDetailsChange}
                  placeholder="Currency (e.g., KES)"
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="date" className="form-label">Date</label>
                <Calendar
                  id="date"
                  name="date"
                  value={poDetails.date}
                  onChange={(e) => setPoDetails({...poDetails, date: e.value})}
                  showIcon
                  dateFormat="dd/mm/yy"
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="budget" className="form-label">Budget (Optional)</label>
                <InputText
                  id="budget"
                  name="budget"
                  value={poDetails.budget}
                  onChange={handlePoDetailsChange}
                  placeholder="Budget allocation"
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-12">
                <label htmlFor="internalNotes" className="form-label">Internal Notes</label>
                <InputTextarea
                  id="internalNotes"
                  name="internalNotes"
                  value={poDetails.internalNotes}
                  onChange={handlePoDetailsChange}
                  rows={3}
                  placeholder="Internal notes visible only to staff"
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-12">
                <label htmlFor="supplierNotes" className="form-label">Supplier Notes</label>
                <InputTextarea
                  id="supplierNotes"
                  name="supplierNotes"
                  value={poDetails.supplierNotes}
                  onChange={handlePoDetailsChange}
                  rows={3}
                  placeholder="Notes visible to the supplier"
                />
              </div>
            </div>

            {/* Item summary section */}
            <div className="row mb-3">
              <div className="col-md-12">
                <h6>Order Summary</h6>
                {currentResponse.items && currentResponse.items.length > 0 ? (
                  <DataTable value={currentResponse.items} stripedRows scrollable scrollHeight="200px">
                    <Column field="itemName" header="Item" />
                    <Column field="quantity" header="Qty" style={{ width: '80px' }} />
                    <Column field="unitPrice" header="Unit Price" body={rowData =>
                      `${poDetails.currency || 'KES'} ${(rowData.unitPrice || 0).toLocaleString()}`
                    } />
                    <Column field="totalPrice" header="Total" body={rowData =>
                      `${poDetails.currency || 'KES'} ${(rowData.totalPrice || 0).toLocaleString()}`
                    } />
                  </DataTable>
                ) : (
                  <p>No items in this order.</p>
                )}
                
                {currentResponse.items && currentResponse.items.length > 0 && (
                  <div className="d-flex justify-content-end mt-2">
                    <strong>Total: {poDetails.currency || 'KES'} {currentResponse.items.reduce((sum, item) => sum + (item.totalPrice || 0), 0).toLocaleString()}</strong>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Dialog>
    </Layout>
  );
};

export default RFQResponses;